import React from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import useModal from '../../../hooks/useModal.ts';
import styles from './styles.module.css';
import StatusCard from '../../../components/StatusCard/index.tsx';
import { Calendar as CalendarIcon } from '@livingdesign/icons';
import InstructionsSidePanel from '../../../components/InstructionsSidePanel/index.tsx';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { ihutActions, ihutSelectors } from '../../../redux/slices/ihut.ts';
import { IhutActivity } from '../../../api/ihut.ts';
import { Trans, useTranslation } from 'react-i18next';
import useSnackbar from '../../../hooks/useSnackbar.ts';
import { AUTO_HIDE_DURATION } from '../../../constants/common.js';
import { Snackbar } from '@material-ui/core';
import { logger } from '../../../utils/logger.js';
import { isAfterCurrentDate } from '../../../utils/date.ts';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const TestInstructionsCard = () => {
  const { snackbarState, openSnackbar, closeSnackbar } = useSnackbar();

  const { t } = useTranslation('ihut');
  const activity = useAppSelector(
    ihutSelectors.selectSelectedActivity
  ) as IhutActivity;
  const dispatch = useAppDispatch();

  const isRead = activity.testingCompleted;
  const surveyStartDate =
    activity.survey?.type === 'FEEDBACK' ? activity.survey.startTime : null;
  const hasSurveyStarted =
    activity.survey?.type === 'FEEDBACK' &&
    !isAfterCurrentDate(activity.survey.startTime);
  const { isOpen, openModal, closeModal } = useModal();

  const handleReadClick = () => {
    // if (!hasSurveyStarted) return;
    logger.clickStream('ihut_instructions_read_click', {
      panelistId: localStorage.getItem('panelistId') || '',
      projectId: activity.ihutProjectId
    });
    dispatch(ihutActions.updatePanelistState({ testing: true }))
      .unwrap()
      .then(() => {
        closeModal();
      })
      .catch((err) => {
        logger.error(
          'Error updating ihut panelist state to testing completed',
          err
        );
        openSnackbar(err?.message || t('somethingWentWrong', { ns: 'common' }));
      });
  };

  const handleCardClick = () => {
    if (isRead) return;
    openModal();
  };

  let cardTagProps = {};
  if (isRead)
    cardTagProps = {
      showTag: isRead,
      tagProps: {
        text: t('read', { ns: 'common' }),
        variant: 'tertiary',
        color: 'green'
      }
    };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarState.open}
        message={snackbarState.message}
        autoHideDuration={AUTO_HIDE_DURATION}
        onClose={closeSnackbar}
      />
      <StatusCard
        status="test-instructions"
        {...cardTagProps}
        imgSrc={
          isRead
            ? '/assets/images/ihut/celebration-circle.png'
            : '/assets/images/ihut/instructions-board.png'
        }
        extraClasses={styles.testInstructionsCard}
        onClick={handleCardClick}
      >
        {activity.shortId && (
          <StatusCard.Caption>
            {t('selectedActivity.id', { activityId: activity.shortId })}
          </StatusCard.Caption>
        )}
        <StatusCard.Heading>
          {t('testStep.intructionsCard.title')}
        </StatusCard.Heading>
        <StatusCard.Text>
          {t('testStep.intructionsCard.subtitle')}
        </StatusCard.Text>
        <StatusCard.Text>
          <Trans
            t={t}
            i18nKey="testStep.intructionsCard.body"
            values={{
              duration: dayjs.duration(activity.testingDuration).humanize()
            }}
          >
            Testing period:{' '}
            <StatusCard.Text weight={700}>7 days</StatusCard.Text>
          </Trans>
        </StatusCard.Text>
        <StatusCard.Row extraClasses={styles.surveyDateRow}>
          <div className={styles.icon}>
            <CalendarIcon size="small" />
          </div>
          <StatusCard.Caption extraClasses={styles.blueColor}>
            {hasSurveyStarted
              ? t('testStep.intructionsCard.surveyAvailable')
              : t('testStep.intructionsCard.surveyStarts')}
            <StatusCard.Caption extraClasses={styles.blueColor} weight={700}>
              {hasSurveyStarted
                ? t('testStep.intructionsCard.available')
                : surveyStartDate
                ? dayjs(surveyStartDate).format('MM/DD/YYYY')
                : dayjs(activity.createTime)
                    .add(dayjs.duration(activity.testingDuration))
                    .format('MM/DD/YYYY')}
            </StatusCard.Caption>
          </StatusCard.Caption>
        </StatusCard.Row>
      </StatusCard>
      <InstructionsSidePanel
        isOpen={isOpen}
        position="right"
        title={t('testStep.instructionsPanelTitle', {
          activityName: activity.name
        })}
        bodyContent={activity.customerInstructions}
        handleReadClick={handleReadClick}
        onClose={closeModal}
        // disabled={!hasSurveyStarted}
      />
    </>
  );
};

export default TestInstructionsCard;
